import { Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core'
import { trigger, state, style, transition, animate } from '@angular/animations'
import { Subject, Subscription, interval } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { StickyOffsetService } from '../services/sticky-offset.service'
import { ApplicationCreatorState } from 'src/app/account/services/application-creator-state.service'

@Component({
  selector: 'stacksets-instructions',
  templateUrl: './stacksets-instructions.component.html',
  styleUrls: ['./stacksets-instructions.component.scss'],
  standalone: false,
  animations: [
    trigger('slideDown', [
      state('collapsed', style({ height: '0', overflow: 'hidden' })),
      state('expanded', style({ height: '*', overflow: 'visible' })),
      transition('collapsed <=> expanded', [animate('200ms ease-in-out')]),
    ]),
  ],
})
export class StacksetsInstructionsComponent implements OnInit, OnDestroy {
  @Input() sourceStackSetTemplateUrl: string
  @Input() targetStackSetTemplateUrl: string
  @Input() sourceTemplateBucket: string
  @Input() sourceTemplateKey: string
  @Input() targetTemplateBucket: string
  @Input() targetTemplateKey: string
  @Input() sourceAccountId: string
  @Input() sourceRegion: string
  @Input() targetAccountId: string
  @Input() targetRegion: string

  // Access state inputs - to determine if onboarding is needed
  @Input() sourceStackName: string
  @Input() targetStackName: string

  // Whether the stacks are managed by StackSet (from backend access check)
  @Input() sourceManagedByStackSet: boolean = false
  @Input() targetManagedByStackSet: boolean = false

  // Management account ID (if known, passed from parent)
  @Input() managementAccountId: string = ''

  // Reference element ID for sticky offset calculation
  @Input() stickyReferenceId: string = ''

  // Testing state - true when access check is in progress
  @Input() testingSource: boolean = false
  @Input() testingTarget: boolean = false

  // Error state - true when access check failed with an error
  @Input() sourceError: boolean = false
  @Input() targetError: boolean = false

  // Completed test counts for retry messaging
  @Input() sourceTestCount: number = 0
  @Input() targetTestCount: number = 0

  // Migration mode - when true, adjusts messaging for CF → StackSets migration
  // (existing stacks are expected, cleanup guidance shown after success)
  @Input() migrationMode: boolean = false

  // Hide the component's h2/intro when parent provides its own header
  @Input() hideHeader: boolean = false

  // Emitted when user clicks "Test Access" button - emits 'source' or 'target'
  @Output() testAccess = new EventEmitter<'source' | 'target'>()

  // Emitted when user clicks "Finish Set Up" after migration completes
  @Output() finishSetup = new EventEmitter<void>()

  // Track which commands have been copied
  copiedCommands: Set<string> = new Set()

  // Expanded sections (all closed by default)
  expandedSections: Set<string> = new Set()

  // Sticky offset for step headers
  stickyOffset: number = 0
  private destroy$ = new Subject<void>()

  // Polling for step 3 (deploy section)
  private pollingSubscription: Subscription | null = null
  private readonly POLLING_INTERVAL_MS = 10000

  // Local state fallback when ApplicationCreatorState is not available (e.g., in migration flow)
  private _localManagementAccountId: string = ''
  private _localPrimaryStackSetName: string = ''
  private _localRecoveryStackSetName: string = ''

  constructor(
    public uiService: UiService,
    private stickyOffsetService: StickyOffsetService,
    @Optional() private creatorState: ApplicationCreatorState
  ) {}

  // Get unique account IDs (avoids showing duplicates when source and target are same account)
  get uniqueAccountIds(): string[] {
    const accounts = new Set<string>()
    if (this.sourceAccountId) accounts.add(this.sourceAccountId)
    if (this.targetAccountId) accounts.add(this.targetAccountId)
    return Array.from(accounts)
  }

  // User-entered values - use ApplicationCreatorState if available, otherwise local state
  get managementAccountIdInput(): string {
    return this.creatorState?.stacksetsManagementAccountId ?? this._localManagementAccountId
  }
  set managementAccountIdInput(value: string) {
    if (this.creatorState) {
      this.creatorState.stacksetsManagementAccountId = value
      this.creatorState.markDirty()
    } else {
      this._localManagementAccountId = value
    }
  }

  get primaryStackSetName(): string {
    return this.creatorState?.stacksetsPrimaryName ?? this._localPrimaryStackSetName
  }
  set primaryStackSetName(value: string) {
    if (this.creatorState) {
      this.creatorState.stacksetsPrimaryName = value
      this.creatorState.markDirty()
    } else {
      this._localPrimaryStackSetName = value
    }
  }

  get recoveryStackSetName(): string {
    return this.creatorState?.stacksetsRecoveryName ?? this._localRecoveryStackSetName
  }
  set recoveryStackSetName(value: string) {
    if (this.creatorState) {
      this.creatorState.stacksetsRecoveryName = value
      this.creatorState.markDirty()
    } else {
      this._localRecoveryStackSetName = value
    }
  }

  ngOnInit(): void {
    // All sections start closed - user expands as needed

    // Subscribe to sticky offset changes if a reference element is provided
    if (this.stickyReferenceId) {
      this.stickyOffsetService
        .getOffset$(this.stickyReferenceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((offset) => {
          this.stickyOffset = offset
        })
    }
  }

  ngOnDestroy(): void {
    this.stopPolling()
    this.destroy$.next()
    this.destroy$.complete()
  }

  // Section toggle
  toggleSection(section: string): void {
    if (this.expandedSections.has(section)) {
      this.expandedSections.delete(section)
      if (section === 'deploy') {
        this.stopPolling()
      }
    } else {
      this.expandedSections.add(section)
      if (section === 'deploy') {
        this.startPolling()
      }
    }
  }

  private startPolling(): void {
    this.stopPolling()
    this.pollingSubscription = interval(this.POLLING_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Skip polling for endpoints with errors or already being tested
        if (this.needsSourceOnboarding && !this.testingSource && !this.sourceError) {
          this.testAccess.emit('source')
        }
        if (this.needsTargetOnboarding && !this.testingTarget && !this.targetError) {
          this.testAccess.emit('target')
        }
      })
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe()
      this.pollingSubscription = null
    }
  }

  isExpanded(section: string): boolean {
    return this.expandedSections.has(section)
  }

  // Copy feedback
  onCopied(commandId: string): void {
    this.copiedCommands.add(commandId)
    setTimeout(() => {
      this.copiedCommands.delete(commandId)
    }, 2000)
  }

  isCopied(commandId: string): boolean {
    return this.copiedCommands.has(commandId)
  }

  // Download command as shell script
  downloadCommand(commandId: string, command: string): void {
    const content = `#!/bin/bash\n\n${command}\n`
    const blob = new Blob([content], { type: 'text/x-sh' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `arpio-${commandId}.sh`
    a.click()
    URL.revokeObjectURL(url)
  }

  get needsSourceOnboarding(): boolean {
    // In StackSets flow, we check if access is managed by StackSet, not just if any stack exists
    return !this.sourceManagedByStackSet
  }

  get needsTargetOnboarding(): boolean {
    // In StackSets flow, we check if access is managed by StackSet, not just if any stack exists
    return !this.targetManagedByStackSet
  }

  // Check if access exists via CloudFormation but NOT via StackSets
  // Used to show informational message about existing CF stacks
  get hasSourceCfnButNotStackSet(): boolean {
    return !!this.sourceStackName && !this.sourceManagedByStackSet
  }

  get hasTargetCfnButNotStackSet(): boolean {
    return !!this.targetStackName && !this.targetManagedByStackSet
  }

  // Migration complete - both accounts now managed by StackSets
  get migrationComplete(): boolean {
    return this.migrationMode && this.sourceManagedByStackSet && this.targetManagedByStackSet
  }

  // Commands for cleaning up old CloudFormation stacks after migration
  get deleteSourceCfnStackCommand(): string {
    if (!this.sourceStackName) return ''
    return `aws cloudformation delete-stack \\
  --stack-name ${this.sourceStackName} \\
  --region ${this.sourceRegion}`
  }

  get deleteTargetCfnStackCommand(): string {
    if (!this.targetStackName) return ''
    return `aws cloudformation delete-stack \\
  --stack-name ${this.targetStackName} \\
  --region ${this.targetRegion}`
  }

  onTestAccess(scope: 'source' | 'target'): void {
    this.testAccess.emit(scope)
  }

  get sourceRetryText(): string {
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    return this.sourceTestCount ? textOptions[this.sourceTestCount % textOptions.length] : ''
  }

  get targetRetryText(): string {
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    return this.targetTestCount ? textOptions[this.targetTestCount % textOptions.length] : ''
  }

  // Helper for management account placeholder
  get mgmtAccountPlaceholder(): string {
    return this.managementAccountIdInput || this.managementAccountId || '<YOUR-MANAGEMENT-ACCOUNT-ID>'
  }

  // Helpers for StackSet name placeholders
  get primaryStackSetNameValue(): string {
    return this.primaryStackSetName || '<YOUR-PRIMARY-STACKSET-NAME>'
  }

  get recoveryStackSetNameValue(): string {
    return this.recoveryStackSetName || '<YOUR-RECOVERY-STACKSET-NAME>'
  }

  // ==================== COMMAND GENERATORS ====================

  get adminRoleCommand(): string {
    return `aws cloudformation create-stack \\
  --stack-name ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --template-body "$(cat <<'EOF'
AWSTemplateFormatVersion: '2010-09-09'
Description: StackSet Administration Role for Arpio
Parameters:
  ExecutionRoleName:
    Type: String
    Default: ArpioStackSetExecutionRole
Resources:
  AdministrationRole:
    Type: AWS::IAM::Role
    Properties:
      RoleName: ArpioStackSetAdministrationRole
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement:
          - Effect: Allow
            Principal:
              Service: cloudformation.amazonaws.com
            Action: sts:AssumeRole
      Policies:
        - PolicyName: AssumeExecutionRole
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action: sts:AssumeRole
                Resource: !Sub 'arn:aws:iam::*:role/\${ExecutionRoleName}'
EOF
)"`
  }

  get executionRoleCommand(): string {
    return `aws cloudformation create-stack \\
  --stack-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --parameters ParameterKey=AdministrationAccountId,ParameterValue=${this.mgmtAccountPlaceholder} \\
  --template-body "$(cat <<'EOF'
AWSTemplateFormatVersion: '2010-09-09'
Description: StackSet Execution Role for Arpio
Parameters:
  AdministrationAccountId:
    Type: String
    Description: AWS Account ID of the Administration Account
  AdministrationRoleName:
    Type: String
    Default: ArpioStackSetAdministrationRole
Resources:
  ExecutionRole:
    Type: AWS::IAM::Role
    Properties:
      RoleName: ArpioStackSetExecutionRole
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement:
          - Effect: Allow
            Principal:
              AWS: !Sub 'arn:aws:iam::\${AdministrationAccountId}:role/\${AdministrationRoleName}'
            Action: sts:AssumeRole
      ManagedPolicyArns:
        - arn:aws:iam::aws:policy/AdministratorAccess
EOF
)"`
  }

  get createPrimaryStackSetCommand(): string {
    return `aws cloudformation create-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --template-url ${this.sourceStackSetTemplateUrl} \\
  --permission-model SELF_MANAGED \\
  --administration-role-arn arn:aws:iam::${this.mgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --execution-role-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }

  get createRecoveryStackSetCommand(): string {
    return `aws cloudformation create-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --template-url ${this.targetStackSetTemplateUrl} \\
  --permission-model SELF_MANAGED \\
  --administration-role-arn arn:aws:iam::${this.mgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --execution-role-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }

  get deployPrimaryCommand(): string {
    return `aws cloudformation create-stack-instances \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --accounts ${this.sourceAccountId} \\
  --regions ${this.sourceRegion} \\
  --parameter-overrides \\
    ParameterKey=SourceAwsAccountId,ParameterValue=${this.sourceAccountId} \\
    ParameterKey=SourceRegion,ParameterValue=${this.sourceRegion} \\
    ParameterKey=TargetAwsAccountId,ParameterValue=${this.targetAccountId} \\
    ParameterKey=TargetRegion,ParameterValue=${this.targetRegion} \\
    ParameterKey=TemplateS3Bucket,ParameterValue=${this.sourceTemplateBucket} \\
    ParameterKey=TemplateS3Key,ParameterValue=${this.sourceTemplateKey}`
  }

  get deployRecoveryCommand(): string {
    return `aws cloudformation create-stack-instances \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --accounts ${this.targetAccountId} \\
  --regions ${this.targetRegion} \\
  --parameter-overrides \\
    ParameterKey=SourceAwsAccountId,ParameterValue=${this.sourceAccountId} \\
    ParameterKey=SourceRegion,ParameterValue=${this.sourceRegion} \\
    ParameterKey=TargetAwsAccountId,ParameterValue=${this.targetAccountId} \\
    ParameterKey=TargetRegion,ParameterValue=${this.targetRegion} \\
    ParameterKey=TemplateS3Bucket,ParameterValue=${this.targetTemplateBucket} \\
    ParameterKey=TemplateS3Key,ParameterValue=${this.targetTemplateKey}`
  }

  get updateStackSetsCommand(): string {
    return `# Update Primary Access StackSet
aws cloudformation update-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --use-previous-template \\
  --administration-role-arn arn:aws:iam::${this.mgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM

# Update Recovery Access StackSet
aws cloudformation update-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --use-previous-template \\
  --administration-role-arn arn:aws:iam::${this.mgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }

  // ==================== TROUBLESHOOTING COMMANDS ====================

  // Step 1 Troubleshooting
  get verifyAdminRoleCommand(): string {
    return `aws iam get-role --role-name ArpioStackSetAdministrationRole`
  }

  get verifyExecRoleCommand(): string {
    return `aws iam get-role --role-name ArpioStackSetExecutionRole`
  }

  get verifyExecRoleTrustCommand(): string {
    return `aws iam get-role --role-name ArpioStackSetExecutionRole \\
  --query 'Role.AssumeRolePolicyDocument'`
  }

  get checkAdminStackStatusCommand(): string {
    return `# Check Administration Role stack (run from management account)
aws cloudformation describe-stacks \\
  --stack-name ArpioStackSetAdministrationRole \\
  --query 'Stacks[0].{Status:StackStatus,Reason:StackStatusReason}'

# Check Execution Role stack (run from each target account)
aws cloudformation describe-stacks \\
  --stack-name ArpioStackSetExecutionRole \\
  --query 'Stacks[0].{Status:StackStatus,Reason:StackStatusReason}'`
  }

  get deleteAdminStackCommand(): string {
    return `# Delete Administration Role stack (from management account)
aws cloudformation delete-stack --stack-name ArpioStackSetAdministrationRole

# Delete Execution Role stack (from each target account)
aws cloudformation delete-stack --stack-name ArpioStackSetExecutionRole`
  }

  // Step 2 Troubleshooting
  get listStackSetsCommand(): string {
    return `aws cloudformation list-stack-sets --status ACTIVE`
  }

  get describeStackSetCommand(): string {
    return `aws cloudformation describe-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue}`
  }

  get deleteStackSetCommand(): string {
    return `# Only works if the StackSet has no stack instances
aws cloudformation delete-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue}`
  }

  // Step 3 Troubleshooting
  get listStackInstancesCommand(): string {
    return `# List Primary StackSet instances
aws cloudformation list-stack-instances \\
  --stack-set-name ${this.primaryStackSetNameValue}

# List Recovery StackSet instances
aws cloudformation list-stack-instances \\
  --stack-set-name ${this.recoveryStackSetNameValue}`
  }

  get describeStackInstanceCommand(): string {
    return `# Get detailed status of Primary stack instance
aws cloudformation describe-stack-instance \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --stack-instance-account ${this.sourceAccountId} \\
  --stack-instance-region ${this.sourceRegion}

# Get detailed status of Recovery stack instance
aws cloudformation describe-stack-instance \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --stack-instance-account ${this.targetAccountId} \\
  --stack-instance-region ${this.targetRegion}`
  }

  get listStackSetOperationsCommand(): string {
    return `aws cloudformation list-stack-set-operations \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --max-results 5`
  }

  get retryPrimaryInstanceCommand(): string {
    return `aws cloudformation update-stack-instances \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --accounts ${this.sourceAccountId} \\
  --regions ${this.sourceRegion}`
  }

  get retryRecoveryInstanceCommand(): string {
    return `aws cloudformation update-stack-instances \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --accounts ${this.targetAccountId} \\
  --regions ${this.targetRegion}`
  }

  get deleteStackInstanceCommand(): string {
    return `# Delete Primary stack instance
aws cloudformation delete-stack-instances \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --accounts ${this.sourceAccountId} \\
  --regions ${this.sourceRegion} \\
  --no-retain-stacks

# Delete Recovery stack instance
aws cloudformation delete-stack-instances \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --accounts ${this.targetAccountId} \\
  --regions ${this.targetRegion} \\
  --no-retain-stacks`
  }

  // Stack conflict troubleshooting commands
  get deleteSourceConflictStackCommand(): string {
    const sourceStackCmd = `aws cloudformation delete-stack --stack-name ${this.sourceStackName || 'arpio-source-access-YOUR_APP_NAME'} --region ${this.sourceRegion}`
    const targetStackCmd = `aws cloudformation delete-stack --stack-name ${this.targetStackName || 'arpio-target-access-YOUR_APP_NAME'} --region ${this.targetRegion}`
    return `# If conflict is in primary account (${this.sourceAccountId}):
${sourceStackCmd}

# If conflict is in recovery account (${this.targetAccountId}):
${targetStackCmd}`
  }

  get waitForStackDeleteCommand(): string {
    return `# Check stack status (should show DELETE_IN_PROGRESS, then disappear)
aws cloudformation describe-stacks --stack-name ${this.sourceStackName || 'arpio-source-access-YOUR_APP_NAME'} --query 'Stacks[0].StackStatus'`
  }

  // Scroll to a step section by name (prerequisites, create, deploy)
  scrollToStep(stepName: string, openTroubleshooting: boolean = false): void {
    // Expand the section if not already expanded
    if (!this.expandedSections.has(stepName)) {
      this.expandedSections.add(stepName)
    }

    // Find the step section element
    setTimeout(() => {
      const stepSections = document.querySelectorAll('.step-section')
      let targetSection: HTMLElement | null = null

      // Map step names to their index (0-based)
      const stepIndex = { prerequisites: 0, create: 1, deploy: 2 }[stepName]
      if (stepIndex !== undefined && stepSections[stepIndex]) {
        targetSection = stepSections[stepIndex] as HTMLElement
      }

      if (targetSection) {
        // If opening troubleshooting, scroll to the troubleshooting section instead
        if (openTroubleshooting) {
          const troubleshootDetails = targetSection.querySelector(
            'details.troubleshooting-section'
          ) as HTMLDetailsElement
          if (troubleshootDetails) {
            if (!troubleshootDetails.open) {
              troubleshootDetails.open = true
            }
            // Wait for details to expand, then scroll to it
            setTimeout(() => {
              const rect = troubleshootDetails.getBoundingClientRect()
              const absoluteTop = rect.top + window.scrollY
              const offset = 80
              window.scrollTo({
                top: absoluteTop - offset,
                behavior: 'smooth',
              })
            }, 100)
          }
        } else {
          const rect = targetSection.getBoundingClientRect()
          const absoluteTop = rect.top + window.scrollY
          const offset = 80

          window.scrollTo({
            top: absoluteTop - offset,
            behavior: 'smooth',
          })
        }
      }
    }, 100)
  }

  // Scroll to a troubleshooting section by ID
  scrollToTroubleshooting(sectionId: string): void {
    const element = document.getElementById(sectionId)
    if (!element) return

    // Collect all ancestor details elements (outermost first)
    const detailsToOpen: HTMLDetailsElement[] = []
    let current: HTMLElement | null = element

    // If the element itself is a details, include it
    if (element.tagName === 'DETAILS') {
      detailsToOpen.push(element as HTMLDetailsElement)
    }

    // Walk up the DOM tree to find all parent details elements
    while (current) {
      const parentDetails = current.parentElement?.closest('details') as HTMLDetailsElement | null
      if (parentDetails) {
        detailsToOpen.unshift(parentDetails) // Add to beginning (outermost first)
        current = parentDetails
      } else {
        break
      }
    }

    // Open all collapsed details elements with staggered timing
    let delay = 0
    for (const details of detailsToOpen) {
      if (!details.open) {
        setTimeout(() => {
          details.open = true
        }, delay)
        delay += 150 // Stagger each expansion
      }
    }

    // Wait for all expansions to complete before scrolling
    setTimeout(() => {
      this.scrollAndHighlight(element)
    }, delay + 150)
  }

  // Scroll to element with offset and add temporary highlight
  private scrollAndHighlight(element: HTMLElement): void {
    // Calculate position with offset to show section header clearly
    const elementRect = element.getBoundingClientRect()
    const absoluteTop = elementRect.top + window.scrollY
    const offset = 200 // Offset to account for sticky headers and provide context

    window.scrollTo({
      top: absoluteTop - offset,
      behavior: 'smooth',
    })

    // Add highlight class after scroll starts
    setTimeout(() => {
      element.classList.add('highlight-pulse')
    }, 400)

    // Remove highlight class after animation completes (longer duration)
    setTimeout(() => {
      element.classList.remove('highlight-pulse')
    }, 4000)
  }
}
